import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import { plural } from '../lib/format.js';
import { useSession } from '../lib/session.js';

/**
 * The invite landing page, and a copier's account page afterwards. The invite
 * token arrives in the URL fragment (/join#token), which browsers never send to
 * a server. It is posted once, exchanged for an httpOnly cookie, and wiped from
 * the address bar and history.
 */
export default function Join() {
  const { me, loading, setMe } = useSession();
  const navigate = useNavigate();
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [welcome, setWelcome] = useState(false);
  const [busy, setBusy] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    const token = window.location.hash.replace(/^#/, '');
    if (!token || started.current) return;
    started.current = true;
    window.history.replaceState(null, '', '/join');
    setJoining(true);
    api.join(token).then(
      (m) => {
        setMe(m);
        setWelcome(true);
        setJoining(false);
      },
      (err: Error) => {
        setError(err.message);
        setJoining(false);
      },
    );
  }, [setMe]);

  const setCredit = async (credit: boolean): Promise<void> => {
    setBusy(true);
    try {
      setMe((await api.setCredit(credit)).copier);
    } finally {
      setBusy(false);
    }
  };

  const signOut = async (): Promise<void> => {
    setBusy(true);
    try {
      await api.leave();
      setMe(null);
    } finally {
      setBusy(false);
    }
  };

  const start = async (): Promise<void> => {
    const { id } = await api.next();
    navigate(id ? `/clip/${id}` : '/clips');
  };

  if (joining || loading) return <p className="muted">Signing you in...</p>;

  if (error) {
    return (
      <div className="join">
        <h1>That link didn't work</h1>
        <p className="alert is-error">{error}</p>
      </div>
    );
  }

  if (!me) {
    return (
      <div className="join">
        <h1>Copying is by invitation</h1>
        <p>
          Open the invite link WB5SKM sent you. It looks like <code>https://2x3skimmer.com/join#...</code>, and opening
          it signs this browser in as your callsign. Anyone can listen to the clips without one.
        </p>
      </div>
    );
  }

  return (
    <div className="join">
      <h1>{welcome ? `Welcome, ${me.callsign}.` : `Signed in as ${me.callsign}`}</h1>
      {welcome && (
        <p className="lead">
          This browser is signed in as <strong className="call">{me.callsign}</strong>
          {me.name ? ` (${me.name})` : ''}, and stays that way. To copy on another device, open the same link there. Keep
          the link to yourself: anyone who opens it copies as you.
        </p>
      )}
      {!welcome && (
        <p className="muted">
          {plural(me.copies, 'copy', 'copies')} so far{me.name ? `, ${me.name}` : ''}.
        </p>
      )}

      <label className="check credit">
        <input type="checkbox" checked={me.credit} disabled={busy} onChange={(e) => void setCredit(e.target.checked)} />
        <span>
          <strong>List my callsign on the home page</strong> as one of the copiers.{' '}
          <span className="muted">Off unless you tick it. Your copies themselves are never shown to other copiers.</span>
        </span>
      </label>

      <div className="form-actions">
        <button type="button" className="btn-primary btn-big" onClick={() => void start()}>
          {welcome ? 'Start copying' : 'Next clip'}
        </button>
        <button type="button" className="btn-quiet" onClick={() => void signOut()} disabled={busy}>
          Sign this browser out
        </button>
      </div>
      {welcome && (
        <p className="muted small">
          New to this? Read <Link to="/guide">How to copy</Link> first. It's short.
        </p>
      )}
    </div>
  );
}
